import type { Expr } from '@gymcloud/mixedscript'

/**
 * What a static value is allowed to call — the built-ins of the language. A block sees more
 * (`sessions`, `history`, its own locals), but a block is edited as code, where the evaluator
 * reports its own errors. The fields of the board hold static values, and a static value that
 * names anything else is a typo on its way into the script — `abckg` parses as an identifier,
 * so parsing alone will not catch it.
 */
const BUILTINS = new Set([
  'e1rm',
  'rpe',
  'range',
  'amrap',
  'min',
  'max',
  'floor',
  'ceil',
  'round',
  'abs',
])

/** What is wrong with a static expression, or null when nothing is. */
export function staticExprProblem(value: Expr): string | null {
  switch (value.type) {
    case 'number':
    case 'text':
    case 'hole':
      return null
    case 'name':
      return value.name === 'true' || value.name === 'false'
        ? null
        : `“${value.name}” means nothing here`
    case 'call': {
      const callee = value.callee
      if (callee.type !== 'name' || !BUILTINS.has(callee.name)) {
        return callee.type === 'name'
          ? `“${callee.name}” is not something a set line can call`
          : 'A set line can only call the built-ins'
      }
      for (const arg of value.args) {
        const problem = staticExprProblem(arg)
        if (problem !== null) {
          return problem
        }
      }
      for (const [, arg] of value.named) {
        const problem = staticExprProblem(arg)
        if (problem !== null) {
          return problem
        }
      }
      return null
    }
    case 'unary':
      return staticExprProblem(value.operand)
    case 'binary':
      return staticExprProblem(value.left) ?? staticExprProblem(value.right)
    case 'ternary':
      return (
        staticExprProblem(value.condition) ??
        staticExprProblem(value.then) ??
        staticExprProblem(value.otherwise)
      )
    case 'array': {
      for (const item of value.items) {
        const problem = staticExprProblem(item)
        if (problem !== null) {
          return problem
        }
      }
      return null
    }
    // Members, indexing and arrows read locals, and only a block has any.
    case 'member':
    case 'index':
    case 'arrow':
      return 'Only a code line can do that'
  }
}
